import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setSearchText } from "../../../redux/historicalSlice";
import AlertMessage from "../../../components/AlertMessage";
import { ClaimHistoricCSVRequestDto } from "../types";
import Search from "../../../components/Search.tsx";
import { useTranslation } from "react-i18next";
import { Col, Form, Row } from "react-bootstrap";
import { useAlert } from "../../../hooks/useAlert.ts";
import SpinerLoader from "../../../components/SpinerLoader.tsx";
import ModalGlobal from "../../../components/AppModal/ModalGlobal.tsx";
import { useClaimModal } from "../hooks/useClaimModal.tsx";
import { setFilterByCurrentUser } from "../../../redux/filtersSlice.ts";
import HistoricalClaimsTable from "../components/dashboard/HistoricalClaimsTable.tsx";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse.ts";
import { useGetHistoricClaims } from "../hooks/useGetHistoricClaims.ts";
import { PaginationFilterDto } from "../../../types/Pagination.ts";
import { exportCsvClaim } from "../api/exportHistoryCsv.tsx";
import { UseSelectBasicObject } from "../../../types/UseSelectBasicObject.ts";
import { addAuditLog } from "../../auditLogs/api/auditApi.ts";
import { AuditActionEnum } from "../../auditLogs/types/index.ts";


export default function HistoricalClaims() {
    const [searchTerm, setSearchTerm] = useState('');
    const { t } = useTranslation(['common', 'claim']);

    const dispatch = useDispatch();
    const itemsPerPage = useSelector((state: RootState) => state.historical.claim.itemsPerPage);
    const currentPage = useSelector((state: RootState) => state.historical.claim.currentPage);
    const currentUser = useSelector((state: RootState) => state.currentUserInfo.data);
    const { filterByCurrentUser, selectedFilters } = useSelector((state: RootState) => state.filters.claims);

    const { data: claimsHistoryData, isLoading: isFetchingHistoricalData } = useGetHistoricClaims();

    
    //alerta mensaje
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });

    //modal 
    const { showModal, modalData, error, endDate, closeModal, openUploadModal, isLoading } = useClaimModal();

    const CLAIM_TABLE_HEADERS = {
        id: t("claim:historic.tableHeaderId"),
        namePacient: t("claim:historic.tableHeaderNamePacient"),
        cip: t("claim:historic.tableHeaderCIP"),
        eventDate: t("claim:historic.tableHeaderEventDate"),
        entryDate: t("claim:historic.tableHeaderEntryDate"),
        creationDate: t("claim:historic.tableHeaderCreationDate"),
        alert: t("claim:historic.tableHeaderAlert"),
        state: t("claim:historic.tableHeaderSate"),
        comment: t("claim:historic.tableHeaderComment"),
        timeline: t("claim:historic.tableHeaderTimeline"),
        actions: t("common:actions")
    };

    const headerItemsArray: UseSelectBasicObject[] = [
        {
            id: "Id",
            name: t("claim:export.headerId")
        },
        {
            id: "RequestType",
            name: t("claim:export.headerInputType")
        },
        {
            id: "NameAndSurname",
            name: t("claim:export.headerPersonClaimant.nameAndSurname")
        },
        {
            id: "RelationshipWithAffectedPerson",
            name: t("claim:export.headerPersonClaimant.relationshipWithAffected")
        },
        {
            id: "BirthDate",
            name: t("claim:export.headerPersonClaimant.birthDate")
        },
        {
            id: "Cip",
            name: t("claim:export.headerPersonClaimant.cip")
        },
        {
            id: "Dni",
            name: t("claim:export.headerPersonClaimant.dni")
        },
        {
            id: "Address",
            name: t("claim:export.headerPersonClaimant.address")
        },
        {
            id: "City",
            name: t("claim:export.headerPersonClaimant.city")
        },
        {
            id: "ZipCode",
            name: t("claim:export.headerPersonClaimant.zipCode")
        },
        {
            id: "Phone",
            name: t("claim:export.headerPersonClaimant.phone")
        },
        {
            id: "Mobile",
            name: t("claim:export.headerPersonClaimant.mobile")
        },
        {
            id: "Email",
            name: t("claim:export.headerPersonClaimant.email")
        },
        {
            id: "PersonAffected.NameAndSurname",
            name: t("claim:export.headerPersonAffected.nameAndSurname")
        },
        {
            id: "PersonAffected.MedicalHistoryNumber",
            name: t("claim:export.headerPersonAffected.medicalHistoryNumber")
        },
        {
            id: "PersonAffected.BirthDate",
            name: t("claim:export.headerPersonAffected.birthDate")
        },
        {
            id: "PersonAffected.Cip",
            name: t("claim:export.headerPersonAffected.cip")
        },
        {
            id: "PersonAffected.Dni",
            name: t("claim:export.headerPersonAffected.dni")
        },
        {
            id: "PersonAffected.Address",
            name: t("claim:export.headerPersonAffected.address")
        },
        {
            id: "PersonAffected.City",
            name: t("claim:export.headerPersonAffected.city")
        },
        {
            id: "PersonAffected.ZipCode",
            name: t("claim:export.headerPersonAffected.zipCode")
        },
        {
            id: "PersonAffected.Phone",
            name: t("claim:export.headerPersonAffected.phone")
        },
        {
            id: "PersonAffected.Mobile",
            name: t("claim:export.headerPersonAffected.mobile")
        },
        {
            id: "PersonAffected.Email",
            name: t("claim:export.headerPersonAffected.email")
        },
        {
            id: "EventDate",
            name: t("claim:export.headerEventDate")
        },
        {
            id: "EventTime",
            name: t("claim:export.headerEventTime")
        },
        {
            id: "EntryDate",
            name: t("claim:export.headerEntryDate")
        },
        {
            id: "CreatedDate",
            name: t("claim:export.headerCreationDate")
        },
        {
            id: "Alert",
            name: t("claim:export.headerAlert")
        },
        {
            id: "State",
            name: t("claim:export.headerSate")
        },
        {
            id: "Comment",
            name: t("claim:export.headerComment")
        },
        {
            id: "TimelineDays",
            name: t("claim:export.headerTimeline")
        },
        {
            id: "Professionals.NameAndSurname",
            name: t("claim:export.headerClaimProfesionalDto.nameAndSurname")
        },
        {
            id: "Professionals.ServiceIndicator",
            name: t("claim:export.headerClaimProfesionalDto.serviceIndicator")
        },
        {
            id: "Professionals.RegisteredNumber",
            name: t("claim:export.headerClaimProfesionalDto.registeredNumber")
        },
        {
            id: "Professionals.TypeProfessional",
            name: t("claim:export.headerClaimProfesionalDto.typeProfessional")
        },
        {
            id: "Professionals.Email",
            name: t("claim:export.headerClaimProfesionalDto.email")
        },
        {
            id: "InternalReportRequestDate",
            name: t("claim:export.headerInternalReportRequestDate")
        },
        {
            id: "InternalReportDate",
            name: t("claim:export.headerInternalReportDate")
        },
        {
            id: "DeliveryDate",
            name: t("claim:export.headerDeliveryDate")
        },
        {
            id: "ExpedientGRE",
            name: t("claim:export.headerGREFile")
        },
        {
            id: "ShipmentStatusToGRE",
            name: t("claim:export.headerShipmentStatusToGRE")
        },
        {
            id: "RegisterNumber",
            name: t("claim:export.headerRegisterNumber")
        },
        {
            id: "RegisterDate",
            name: t("claim:export.headerRegisterDate")
        },
        {
            id: "ClaimInOutFormatInput",
            name: t("claim:export.headerInputFormat")
        },
        {
            id: "Origin",
            name: t("claim:export.headerClaimOrigins")
        },
        {
            id: "OtherOrigin",
            name: t("claim:export.headerOtherOrigin")
        },
        {
            id: "ClosingDate",
            name: t("claim:export.headerClosedData")
        },
        {
            id: "ClosingPassedDays",
            name: t("claim:export.headerDaysPassed")
        },
        {
            id: "Resolution",
            name: t("claim:export.headerResolution")
        },
        {
            id: "DemandDerivation",
            name: t("claim:export.headerReferraToAnotherManagementPoint")
        },
        {
            id: "ClaimInOutFormatOutput",
            name: t("claim:export.headerOutputFormat")
        }

        
    ];

    const [selectedCsvFields, setSelectedCsvFields] = useState<string[]>([]);

    const exportFieldOptions: UseSelectBasicObject[] = headerItemsArray
        .filter((h): h is NonNullable<typeof h> => !!h)
        .map((h) => ({ id: h.id, name: t(h.name) }));

    const handleExportCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        setSelectedCsvFields((prev) =>
            checked ? [...prev, value] : prev.filter((f) => f !== value)
        );
    };

    const handleSelectAllCsvFields = (checked: boolean) => {
        setSelectedCsvFields(checked ? exportFieldOptions.map((f) => String(f.id)) : []);
    };

    const exportToCsv = async () => {
        try {
            const exportFieldOptionsSelected = headerItemsArray.filter(item =>
                selectedCsvFields.includes(item.id)
            ).map((h) => ({ property: h.id, header: t(h.name) }));
        

            const paginationFilters: PaginationFilterDto =
            {
                pageNumber: 1,
                pageSize: 10,
                search: searchTerm,
                filterByCurrentUser :filterByCurrentUser,
                filters: selectedFilters,
                selectedOrganizationalStructureIds: currentUser.selectedOrganizationalStructureIds
            };

            const body: ClaimHistoricCSVRequestDto = {
                csvColumnsDto: exportFieldOptionsSelected,
                paginationFilterDto: paginationFilters,
            };

            await exportCsvClaim(body);
            await addAuditLog({
                entity: 'claimHistorical',
                action: AuditActionEnum.Print,
                source: window.location.pathname,
                description: "Historical claims dashboard report printed"
            });
            closeModal();
            showAlert({
                message: t("claim:export.claimExportedCSVSSuccessfully"),
                type: 'success',
                duration: 8000
            });

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage =
                fetchError?.message ||
                    t("claim:export.claimExportedCSVSError");
            closeModal();

            showAlert({
                message: errorMessage,
                type: 'danger',
                duration: 8000
            });
        }
    }

    const handleExportCsv = async () => {
        openUploadModal();
    }

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByCurrentUser({ table: 'claims', filterByCurrentUser: e.target.checked }))
    }

    const handleClearSearchText = () => {
        setSearchTerm('');
        dispatch(setSearchText({ table: "claim", text: '', goFirstPage: true }))
    }
    return (
        <>
            {/* Busqueda, alerta y tabla */}
            <Row>
                <ModalGlobal
                    show={showModal}
                    onClose={closeModal}
                    title={modalData.title}
                    contentType={modalData.type}
                    subtitle={modalData.subtitle}
                    saveText={t("common:modalActionOK")}
                    closeText={t("common:modalActionNOTOK")}
                    error={error}
                    endDate={endDate}
                    onSave={() => exportToCsv()}
                    isLoading={isLoading}
                    optionsMap={exportFieldOptions}
                    showOptions={true}
                    selectedOptions={selectedCsvFields}
                    handleCheckboxChange={handleExportCheckboxChange}
                    handleSelectAllChange={handleSelectAllCsvFields}
                />

                {isFetchingHistoricalData ? (
                    <div className="justify-content-center">
                        <SpinerLoader />
                    </div>
                ) : (
                    <Col xs={12}>
                        {isVisible && (
                            <AlertMessage
                                message={alert!.message}
                                type={alert!.type}
                                onClose={hideAlert}
                            />
                        )}

                        <div style={{ marginBottom: '5px' }}>
                            <Search
                                searchText={searchTerm || ''}
                                onChange={setSearchTerm}
                                onSearch={() =>
                                    dispatch(
                                        setSearchText({
                                            table: "claim",
                                            text: searchTerm || '',
                                            goFirstPage: true
                                        })
                                    )
                                }
                                onClear={handleClearSearchText}
                            />
                        </div>

                        <Form>
                            <Form.Check
                                className="my-4"
                                type="switch"
                                id="custom-switch"
                                label={t("common:buttons.toggleAssignUser")}
                                checked={filterByCurrentUser}
                                onChange={handleToggle}
                            />
                        </Form>

                        {claimsHistoryData && (
                            <HistoricalClaimsTable
                                historyData={claimsHistoryData}
                                currentPage={currentPage}
                                pageSize={itemsPerPage}
                                totalPages={claimsHistoryData.totalPages}
                                handleExportCsv={handleExportCsv}
                                headerItemsArray={CLAIM_TABLE_HEADERS}
                            />
                        )}
                    </Col>
                )}
            </Row>
        </>
    );  
}




